import { useLang } from '../i18n'
import type { KbSourceRecord, KbSourceSyncStatus } from '../lib/api'
import { KbSourceSyncCell } from './KbSourceSyncCell'

/** The translator `useLang()` hands out — keyed to the dictionary, so a typo is a type error. */
type Translate = ReturnType<typeof useLang>['t']

/** "Whole library" or "1 incl · 3 excl", with the patterns themselves on the title. */
function scopeLabel(src: KbSourceRecord, t: Translate): string {
  if (src.folders.length === 0 && src.excludeFolders.length === 0) return t('kbsrc_scope_whole')
  return t('kbsrc_scope_counts')
    .replace('{incl}', String(src.folders.length))
    .replace('{excl}', String(src.excludeFolders.length))
}

function scopeTitle(src: KbSourceRecord): string | undefined {
  const lines = [
    ...src.folders.map((f) => `+ ${f}`),
    ...src.excludeFolders.map((f) => `− ${f}`),
  ]
  return lines.length > 0 ? lines.join('\n') : undefined
}

/**
 * The catalogue as a table — one row per corporate source, whether enabled or not.
 *
 * Access state is TEXT, never colour alone: "Accessible" and "Restricted" are the words, and the
 * badge tint is decoration on top of them. This is the one column where a misread is a
 * confidentiality mistake, so it does not get to depend on someone distinguishing two hues.
 *
 * A hidden `<caption>` names the table for a screen reader, which a `<table>` inside a settings
 * card otherwise has nothing to be introduced by.
 */
export function KbSourceList({
  sources,
  status,
  onEdit,
  onDelete,
}: {
  sources: KbSourceRecord[]
  status: Map<string, KbSourceSyncStatus>
  onEdit: (src: KbSourceRecord) => void
  onDelete: (src: KbSourceRecord) => void
}) {
  const { t } = useLang()

  return (
    <table className="kbtable kbsrc-table">
      <caption className="visually-hidden">{t('kbsrc_table_caption')}</caption>
      <thead>
        <tr>
          <th scope="col">{t('kbsrc_col_name')}</th>
          <th scope="col">{t('kbsrc_col_library')}</th>
          <th scope="col">{t('kbsrc_col_scope')}</th>
          <th scope="col">{t('kbsrc_col_access')}</th>
          <th scope="col">{t('kbsrc_col_state')}</th>
          <th scope="col">{t('kbsrc_col_indexed')}</th>
          <th scope="col">{t('kbsrc_col_files')}</th>
          <th scope="col">{t('kbsrc_col_actions')}</th>
        </tr>
      </thead>
      <tbody>
        {sources.map((src) => {
          const st = status.get(src.id)
          return (
            <tr key={src.id}>
              <td>
                <div className="kbsrc-name">
                  <b>{src.name}</b>
                  <span className="kbsrc-muted kbsrc-sub">{src.id}</span>
                </div>
              </td>
              {/* A site URL is far longer than its share of eight columns, and its *tail* is what
                  identifies it — every row starts with the same tenant host. So the column is
                  capped and the value is cropped from the LEFT, keeping the right-hand side
                  readable, with the whole URL on the tooltip. The `<bdi>` is load-bearing: the
                  left crop is done by laying the line out right-to-left, which would otherwise
                  move a trailing `/` to the wrong end of the string. */}
              <td className="kbsrc-muted kbsrc-lib">
                <a
                  className="kbsrc-lib-link"
                  href={src.siteUrl}
                  title={src.siteUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <bdi>{src.siteUrl}</bdi>
                </a>
              </td>
              <td title={scopeTitle(src)}>
                {scopeLabel(src, t)}
                {/* The sync already refused to seal this drive as indexed; without this line the
                    row would look configured while the source indexes nothing, run after run. In
                    words, not just amber — the colour is decoration on top of them. */}
                {st?.scopeMatchedNothing && (
                  <span className="kbsrc-state kbsrc-state-warn kbsrc-scope-warn">
                    {t('kbsrc_scope_matched_nothing')}
                  </span>
                )}
              </td>
              <td>
                {src.accessible ? (
                  <span className="badge cat">{t('kbsrc_accessible')}</span>
                ) : (
                  <span className="badge warn">
                    {t('kbsrc_restricted_as').replace(
                      '{alias}',
                      src.displayAlias ?? src.name,
                    )}
                  </span>
                )}
              </td>
              <td>
                <span className={src.enabled ? 'badge cat' : 'badge'}>
                  {src.enabled ? t('kbsrc_enabled') : t('kbsrc_disabled')}
                </span>
              </td>
              <td>
                {st ? (
                  <KbSourceSyncCell status={st} />
                ) : (
                  <span className="kbsrc-muted">{t('kbsrc_never_indexed')}</span>
                )}
              </td>
              <td className="kbsrc-muted">
                {st && st.files.total > 0
                  ? t('kbsrc_files_summary')
                      .replace('{indexed}', String(st.files.indexed))
                      .replace('{skipped}', String(st.files.skipped))
                      .replace('{failed}', String(st.files.failed))
                  : '—'}
              </td>
              <td className="kbsrc-acts">
                {/* The buttons are laid out by an inner wrapper, never by the cell itself: a
                    `<td>` made into a flex container stops stretching to the row's height, and
                    its bottom border then draws above the rest of the row — a row separator with
                    a visible step in it. */}
                <div className="kbsrc-acts-row">
                  {/* Each button carries the source's name in its accessible name: eight rows of
                      "Edit" would otherwise be eight identical buttons to anyone not looking at
                      the row they sit in. */}
                  <button
                    type="button"
                    className="mini ghost"
                    aria-label={t('kbsrc_edit_of').replace('{name}', src.name)}
                    onClick={() => onEdit(src)}
                  >
                    {t('kbsrc_edit')}
                  </button>
                  <button
                    type="button"
                    className="mini no"
                    aria-label={t('kbsrc_delete_of').replace('{name}', src.name)}
                    onClick={() => onDelete(src)}
                  >
                    {t('kbsrc_delete')}
                  </button>
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
